import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

const effectiveDateSchema = z.iso.date()
const instantSchema = z.iso.datetime({ offset: true })
const skillKeySchema = z.string().trim().min(1).max(80).regex(/^[a-z0-9][a-z0-9._-]*$/)

export const workerSkillRecordV1Schema = z.object({
  workerSkillId: entityIdSchema,
  workerId: entityIdSchema,
  skillKey: skillKeySchema,
  effectiveFrom: effectiveDateSchema,
  effectiveTo: effectiveDateSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (value.effectiveTo !== null && value.effectiveTo < value.effectiveFrom) {
    context.addIssue({
      code: "custom",
      path: ["effectiveTo"],
      message: "Effective end must not precede effective start.",
    })
  }
})

export const operationalResourceCreateV1Schema = z.object({
  resourceId: entityIdSchema,
  resourceType: z.string().trim().min(1).max(80),
  displayName: z.string().trim().min(1).max(160),
  capacityUnits: z.number().int().positive().max(10_000),
}).strict()

export const availabilityWindowSetV1Schema = z.object({
  availabilityId: entityIdSchema,
  subjectType: z.enum(["Worker", "Crew", "Operational Resource"]),
  subjectId: entityIdSchema,
  startsAt: instantSchema,
  endsAt: instantSchema,
  status: z.enum(["Available", "Unavailable"]),
}).strict().refine((value) => value.endsAt > value.startsAt, {
  path: ["endsAt"],
  message: "Availability end must follow its start.",
})

const crewMemberSchema = z.object({
  crewMemberId: entityIdSchema,
  workerId: entityIdSchema,
  effectiveFrom: effectiveDateSchema,
  effectiveTo: effectiveDateSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (value.effectiveTo !== null && value.effectiveTo < value.effectiveFrom) {
    context.addIssue({
      code: "custom",
      path: ["effectiveTo"],
      message: "Effective end must not precede effective start.",
    })
  }
})

export const crewCreateV1Schema = z.object({
  crewId: entityIdSchema,
  leadWorkerId: entityIdSchema,
  capacityUnits: z.number().int().positive().max(10_000),
  operationalResourceId: entityIdSchema.nullable(),
  members: z.array(crewMemberSchema).min(1).max(50),
}).strict().superRefine((value, context) => {
  const workerIds = value.members.map((member) => member.workerId)
  const memberIds = value.members.map((member) => member.crewMemberId)
  if (!workerIds.includes(value.leadWorkerId)) {
    context.addIssue({
      code: "custom",
      path: ["leadWorkerId"],
      message: "Crew lead must be an effective member.",
    })
  }
  if (new Set(workerIds).size !== workerIds.length) {
    context.addIssue({ code: "custom", path: ["members"], message: "Workers must be unique." })
  }
  if (new Set(memberIds).size !== memberIds.length) {
    context.addIssue({ code: "custom", path: ["members"], message: "Members must be unique." })
  }
})

export const assignmentEligibilityEvaluateV1Schema = z.object({
  evaluationId: entityIdSchema,
  targetType: z.enum(["Worker", "Crew"]),
  targetId: entityIdSchema,
  startsAt: instantSchema,
  endsAt: instantSchema,
  requiredSkillKeys: z.array(skillKeySchema).max(50),
  requiredCapacityUnits: z.number().int().positive().max(10_000),
  resourceIds: z.array(entityIdSchema).max(25),
}).strict().superRefine((value, context) => {
  if (value.endsAt <= value.startsAt) {
    context.addIssue({
      code: "custom",
      path: ["endsAt"],
      message: "Assignment end must follow its start.",
    })
  }
  if (new Set(value.requiredSkillKeys).size !== value.requiredSkillKeys.length) {
    context.addIssue({
      code: "custom",
      path: ["requiredSkillKeys"],
      message: "Required skills must be unique.",
    })
  }
  if (new Set(value.resourceIds).size !== value.resourceIds.length) {
    context.addIssue({
      code: "custom",
      path: ["resourceIds"],
      message: "Resources must be unique.",
    })
  }
})

export const assignmentEligibilityResultV1Schema = z.object({
  entityType: z.enum([
    "worker_skill",
    "operational_resource",
    "availability_window",
    "crew",
    "assignment_evaluation",
  ]),
  entityId: entityIdSchema,
  state: z.string().min(1).max(80),
  version: z.number().int().nonnegative(),
  eligible: z.boolean().nullable(),
  blockerCodes: z.array(z.enum([
    "membership_inactive",
    "branch_access_missing",
    "relationship_inactive",
    "skill_missing",
    "availability_missing",
    "resource_unavailable",
    "capacity_insufficient",
  ])).max(20),
}).strict()

export type WorkerSkillRecordV1 = z.infer<typeof workerSkillRecordV1Schema>
export type OperationalResourceCreateV1 = z.infer<typeof operationalResourceCreateV1Schema>
export type AvailabilityWindowSetV1 = z.infer<typeof availabilityWindowSetV1Schema>
export type CrewCreateV1 = z.infer<typeof crewCreateV1Schema>
export type AssignmentEligibilityEvaluateV1 = z.infer<
  typeof assignmentEligibilityEvaluateV1Schema
>
export type AssignmentEligibilityResultV1 = z.infer<
  typeof assignmentEligibilityResultV1Schema
>
